import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Body1,
  Caption1,
  Card,
} from '@fluentui/react-components';
import type { FluentIcon } from '@fluentui/react-icons';
import {
  DatabaseRegular,
  DocumentRegular,
  ShieldCheckmarkRegular,
  DeleteRegular,
  LockClosedRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import { PageBanner } from '@/components/common/PageBanner';
import { transformrBrand, brandFonts } from '@/theme/transformrTheme';

/* Modelled on the transformr.com.au/privacy layout: a sticky "On this page"
   table of contents on the left, the policy content by heading on the right.
   Clicking a contents item jumps to its section; a scroll-spy highlights the
   section currently in view. App styling (Cards, tokens, brand fonts) intact. */

const SECTIONS: { id: string; label: string; title: string; icon: FluentIcon }[] = [
  { id: 'what-we-store', label: 'What We Store', title: 'What we store', icon: DatabaseRegular },
  { id: 'report-documents', label: 'Report Documents', title: 'Report documents', icon: DocumentRegular },
  { id: 'microsoft-365', label: 'Microsoft 365 Account', title: 'What we read from your Microsoft 365 account', icon: PersonRegular },
  { id: 'security', label: 'How We Protect It', title: 'How your data is protected', icon: LockClosedRegular },
  { id: 'deletion', label: 'Deleting Your Data', title: 'Deleting your data', icon: DeleteRegular },
  { id: 'contact', label: 'Questions?', title: 'Questions?', icon: ShieldCheckmarkRegular },
];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
    maxWidth: '100%',
  },
  // Two columns: sticky contents rail (left) + content (right). Stacks on narrow.
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 15rem) minmax(0, 1fr)',
    gap: tokens.spacingHorizontalXXL,
    alignItems: 'start',
    '@media (max-width: 980px)': {
      gridTemplateColumns: '1fr',
    },
  },
  // Contents rail — sticks to the top of the scroll area as content scrolls.
  toc: {
    position: 'sticky',
    top: 0,
    alignSelf: 'start',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXS,
    '@media (max-width: 980px)': {
      position: 'static',
    },
  },
  tocLabel: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  tocLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    // +2px over SNudge (6px → 8px) top & bottom for a roomier hit target.
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    transitionProperty: 'background-color, color',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${transformrBrand.green}`,
      outlineOffset: tokens.spacingHorizontalXXS,
    },
  },
  tocLinkActive: {
    backgroundColor: transformrBrand.greenSoft,
    color: transformrBrand.greenInk,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: transformrBrand.greenSoft,
      color: transformrBrand.greenInk,
    },
  },
  tocLinkText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
    minWidth: 0,
  },
  section: {
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
    // Offset the smooth-scroll target so it lands clear of the scroll-area top.
    scrollMarginTop: tokens.spacingVerticalL,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalMNudge,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: tokens.spacingHorizontalXL,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase200,
  },
  th: {
    textAlign: 'left',
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  td: {
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    verticalAlign: 'top',
    color: tokens.colorNeutralForeground1,
  },
  footer: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
  },
});

export function DataPrivacyPage() {
  const styles = useStyles();
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  // Scroll-spy: highlight the section that has scrolled into the top of view.
  // The window itself doesn't scroll (the app shell scrolls an inner element),
  // but sections still move within the viewport, so a viewport observer works.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView[0]) setActiveId(inView[0].target.id);
      },
      // Trigger once a section reaches the top ~30% of the viewport.
      { rootMargin: '0px 0px -70% 0px', threshold: 0 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleJump = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <div className={styles.page}>
      <PageBanner
        kicker="Privacy & control"
        title="Your Data & Privacy"
        subtitle="Transparency is important to us. Here's exactly what Transformr Readiness stores, where it's kept, and how you stay in control."
        icon={<ShieldCheckmarkRegular fontSize={14} />}
      />

      <div className={styles.layout}>
        {/* Contents rail */}
        <nav className={styles.toc} aria-label="On this page">
          <span className={styles.tocLabel}>On This Page</span>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={
                activeId === s.id
                  ? `${styles.tocLink} ${styles.tocLinkActive}`
                  : styles.tocLink
              }
              aria-current={activeId === s.id ? 'true' : undefined}
              onClick={() => handleJump(s.id)}
            >
              <s.icon fontSize={16} />
              <span className={styles.tocLinkText}>{s.label}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className={styles.content}>
          {/* What we store */}
          <Card id="what-we-store" className={styles.section}>
            <div className={styles.sectionHeader}>
              <DatabaseRegular fontSize={22} color={tokens.colorBrandForeground1} />
              <Text size={500} weight="semibold">What we store</Text>
            </div>
            <Body1>
              We keep a minimal profile so your details are remembered between visits,
              and we store the reports you generate so you can come back and download them.
            </Body1>

            <Text size={300} weight="semibold" style={{ marginTop: tokens.spacingVerticalS }}>
              Your profile
            </Text>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Field</th>
                  <th className={styles.th}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.td}>Industry</td>
                  <td className={styles.td}>Tailors your report to your sector</td>
                </tr>
                <tr>
                  <td className={styles.td}>Company name</td>
                  <td className={styles.td}>Personalises your report cover page</td>
                </tr>
                <tr>
                  <td className={styles.td}>Your role</td>
                  <td className={styles.td}>Helps us frame recommendations for your audience</td>
                </tr>
                <tr>
                  <td className={styles.td}>Country</td>
                  <td className={styles.td}>Regional context for your report</td>
                </tr>
                <tr>
                  <td className={styles.td}>How you're thinking about AI</td>
                  <td className={styles.td}>Used to personalise the AI insights in your report</td>
                </tr>
              </tbody>
            </table>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Your profile is keyed to your Microsoft 365 account. We don't store passwords
              or authentication credentials — sign-in is handled entirely by Microsoft Entra ID.
            </Caption1>
          </Card>

          {/* Report documents */}
          <Card id="report-documents" className={styles.section}>
            <div className={styles.sectionHeader}>
              <DocumentRegular fontSize={22} color={tokens.colorBrandForeground1} />
              <Text size={500} weight="semibold">Report documents</Text>
            </div>
            <Body1>
              When you generate a Readiness report, the resulting document is stored
              securely so you can download it at any time.
            </Body1>
            <ul className={styles.list}>
              <li>
                <Body1>
                  Reports are securely linked to your organisation — only users within your
                  company can access your reports.
                </Body1>
              </li>
              <li>
                <Body1>
                  Download links are secure and expire automatically in line with Microsoft
                  security best practices. Each time you request a download, a fresh link is generated.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>Reports are automatically deleted after 6 months.</strong> If you'd like
                  to keep a copy, download your report before then.
                </Body1>
              </li>
              <li>
                <Body1>
                  You can delete any of your reports at any time from the <strong>Reports</strong> page.
                </Body1>
              </li>
            </ul>
          </Card>

          {/* What we read from Microsoft 365 */}
          <Card id="microsoft-365" className={styles.section}>
            <div className={styles.sectionHeader}>
              <PersonRegular fontSize={22} color={tokens.colorBrandForeground1} />
              <Text size={500} weight="semibold">What we read from your Microsoft 365 account</Text>
            </div>
            <Body1>
              We only read your basic Microsoft 365 profile — your name and email — to identify
              you and personalise your report. We do not access any of your organisation's content.
            </Body1>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>What we read</th>
                  <th className={styles.th}>What we never access</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.td}>Your name</td>
                  <td className={styles.td}>Your files, documents, and stored content</td>
                </tr>
                <tr>
                  <td className={styles.td}>Your email address</td>
                  <td className={styles.td}>Your email, chats, and meetings</td>
                </tr>
                <tr>
                  <td className={styles.td}></td>
                  <td className={styles.td}>Passwords, MFA, and security settings</td>
                </tr>
                <tr>
                  <td className={styles.td}></td>
                  <td className={styles.td}>Any other organisational or record-level data</td>
                </tr>
              </tbody>
            </table>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              No admin consent is required — we only use your basic, user-level profile.
            </Caption1>
          </Card>

          {/* Security */}
          <Card id="security" className={styles.section}>
            <div className={styles.sectionHeader}>
              <LockClosedRegular fontSize={22} color={tokens.colorBrandForeground1} />
              <Text size={500} weight="semibold">How your data is protected</Text>
            </div>
            <ul className={styles.list}>
              <li>
                <Body1>
                  <strong>Encrypted at rest and in transit</strong> — All stored data is encrypted,
                  and all communication between your browser and our services is secured.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>Secured to your organisation</strong> — Your reports and profile are securely
                  linked to your Microsoft 365 organisation. Only users within your company can
                  access your data.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>Individual, not organisational</strong> — Reports are linked to your individual
                  user account. Other users in your organisation cannot see your reports unless
                  you explicitly download and share the document yourself.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>Time-limited download links</strong> — Report downloads use secure, expiring links that are unique.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>No third-party sharing</strong> — Your data is not shared with, sold to,
                  or accessed by any third party. Where AI is used to personalise your report,
                  only the details you provide in the form are used — none of your organisation's
                  content is ever shared.
                </Body1>
              </li>
            </ul>
          </Card>

          {/* Deletion */}
          <Card id="deletion" className={styles.section}>
            <div className={styles.sectionHeader}>
              <DeleteRegular fontSize={22} color={tokens.colorBrandForeground1} />
              <Text size={500} weight="semibold">Deleting your data</Text>
            </div>
            <ul className={styles.list}>
              <li>
                <Body1>
                  <strong>Reports</strong> — Delete individual reports from the Reports page at any time.
                  All reports are automatically deleted after 6 months. If you need to retain
                  a report for compliance or audit purposes, download a copy before the
                  retention period expires.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>Profile</strong> — Your profile is overwritten each time you update your details.
                  To request full deletion of your profile data, contact us at{' '}
                  <a href="mailto:privacy@transformr.com.au">privacy@transformr.com.au</a>.
                </Body1>
              </li>
              <li>
                <Body1>
                  <strong>Microsoft 365 access</strong> — You can revoke Transformr Readiness's access
                  to your account at any time via{' '}
                  <a href="https://myapps.microsoft.com" target="_blank" rel="noopener noreferrer">
                    myapps.microsoft.com
                  </a>{' '}
                  or your organisation's Azure AD admin portal.
                </Body1>
              </li>
            </ul>
          </Card>

          {/* Contact */}
          <Card id="contact" className={styles.section}>
            <div className={styles.sectionHeader}>
              <ShieldCheckmarkRegular fontSize={22} color={tokens.colorBrandForeground1} />
              <Text size={500} weight="semibold">Questions?</Text>
            </div>
            <Body1>
              If you have any questions about how your data is handled, or would like to request
              data deletion, reach out to us at{' '}
              <a href="mailto:privacy@transformr.com.au">privacy@transformr.com.au</a>.
            </Body1>
          </Card>

          <Caption1 className={styles.footer}>
            Last updated: February 2026. Transformr Pty Ltd, ABN registered in Australia.
          </Caption1>
        </div>
      </div>
    </div>
  );
}
